import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import PersonnalInfoFormQuotation from '../../../../components/Forms/personnal-info-form-quotation';
import BillingInfoFormQuotation from './billing-info-form';
import Hr from '../../../../components/Style/hr';
import Checkbox from '../../../../components/Inputs/checkbox';
import ButtonSecondary from '../../../../components/Buttons/button-secondary';
import Button from '../../../../components/Buttons/button';
import ExplanationStep from '../explanation-step';
import Validator from '../../../../services/validator.service';
import Api from '../../../../services/api.service';
import Helper from '../../../../services/helper.service';
import H3 from '../../../../components/Titles/h3';

export default function AcceptQuotationForm({ quotation, group, quotations = [], returnedData }) {
    const { t } = useTranslation();

    const isGroup = !!group;
    const targets = useMemo(() => {
        if (isGroup) return Array.isArray(quotations) ? quotations : [];
        return quotation ? [quotation] : [];
    }, [isGroup, quotations, quotation]);


    const [action, setAction] = useState();
    const [showModal, setShowModal] = useState(false);
    const [checkbox1Checked, setCheckbox1Checked] = useState(false);
    const [checkboxError, setCheckboxError] = useState(false);

    /* Radio button state */
    const [customerType, setCustomerType] = useState('physique');

    /* Personnal infos */
    const [personnalInfoFormData, setPersonnalInfoFormData] = useState({
        lastname: quotation?.customer?.lastname ?? "",
        firstname: quotation?.customer?.firstname ?? "",
        email: quotation?.customer?.email ?? "",
        birthdate: quotation?.customer?.birthdate ?? "",
        phone: quotation?.customer?.phone ?? ""
    });

    const updatePersonnalInfoFormData = (newData) => { setPersonnalInfoFormData(newData) };
    const [errorsPersonnalInfo, setErrorsPersonnalInfo] = useState({});

    /* Billing info */
    const [billingInfoFormData, setBillingInfoFormData] = useState({
        nameSociety: quotation?.customer?.nameSociety ?? "",
        siret: quotation?.customer?.siret ?? "",
        numTVA: quotation?.customer?.numTVA ?? "",
        country: quotation?.address?.country ?? "",
        city: quotation?.address?.city ?? "",
        address: quotation?.address?.address ?? "",
        postalCode: quotation?.address?.postalCode ?? "",
        address2: quotation?.address?.address2 ?? ""
    });

    const updateBillingInfoFormData = (newData) => { setBillingInfoFormData(newData) };
    const [errorsBillingInfo, setErrorsBillingInfo] = useState({});

    async function handleButtonActionClick(action) {
        if (action === "refused") {
            setAction(action);
            const response = await Api.changeStatusQuotation(quotation._id, action);
            Helper.handleApiResponse(response, t('responses.quotationRefused'), t('responses.quotationError'));
            returnedData(action);

        } else if (action === "modify") {
            setAction(action);

        } else if (action === "cancelModify") {
            setAction(null);

        } else if (action === "accept") {
            // Validate forms
            const tmpErrorsPersonnalInfo = Validator.validatePersonnalInfoFormData(personnalInfoFormData, t);
            const tmpErrorsBillingInfo = Validator.validateBillingInfoFormData(billingInfoFormData, t, customerType);

            setErrorsPersonnalInfo(tmpErrorsPersonnalInfo);
            setErrorsBillingInfo(tmpErrorsBillingInfo);

            const isPersonalInfoValid = Object.keys(tmpErrorsPersonnalInfo).length === 0;
            const isBillingInfoValid = Object.keys(tmpErrorsBillingInfo).length === 0;

            if (!isPersonalInfoValid || !isBillingInfoValid) return;

            // Apply data to 1 or many quotations, set status "sent", save all
            try {
                const updates = targets.map((q) => {
                    const merged = Helper.setDataToQuotation({ ...q }, personnalInfoFormData, billingInfoFormData);
                    merged.mainInfos = { ...(merged.mainInfos || {}), status: 'sent' };
                    return merged;
                });

                // Save sequentially (or Promise.all if your API can handle it)
                for (const u of updates) {
                    // eslint-disable-next-line no-await-in-loop
                    await Api.saveQuotation(u);
                }

                setShowModal(true); // Show CGV modal
            } catch (e) {
                Helper.handleApiResponse?.({ data: { status: false } }, undefined, t('responses.quotationError'));
            }
            return;

        } else if (action === 'acceptCGV') {
            if (!checkbox1Checked) {
                setCheckboxError(true);
                return;
            }
            setCheckboxError(false);

            try {
                // Set all quotations to "accepted"
                const accepted = targets.map((q) => {
                    const clone = { ...q, mainInfos: { ...(q.mainInfos || {}), status: 'accepted' } };
                    return clone;
                });

                for (const a of accepted) {
                    await Api.saveQuotation(a);
                }

                if (isGroup && group?._id) {
                    await Api.changeStatusQuotationGroup(group._id, 'paiement');
                }

                Helper.handleApiResponse({ data: { status: true } }, t('responses.quotationAccepted'), t('responses.quotationError'));
                returnedData('step3');
                setShowModal(false);

            } catch (e) {
                Helper.handleApiResponse?.({ data: { status: false } }, undefined, t('responses.quotationError'));
            }
            return;
        }
    }

    async function handleDownloadQuotationCGV() {
        await Api.downloadQuotationCGV(quotation);
    }

    return (
        <div className='px-4 md:px-0'>
            <div className="flex flex-col gap-5 md:flex-row md:gap-40">
                <div className="w-full md:w-64 flex flex-col gap-5">
                    <ExplanationStep
                        title={t('pageQuotation.step1.personalAndBillingInformation')}
                        description={t('pageQuotation.step1.descriptionPersonalAndBillingInformation')} />
                </div>

                <div className="flex flex-col w-full">
                    <div className="mb-4">
                        <div className="mt-2 flex justify-around">
                            <label className="flex items-center gap-2 cursor-pointer">
                                <input type="radio" name="customerType" value="physique" checked={customerType === 'physique'} onChange={() => setCustomerType('physique')} className="text-main checked:bg-main focus:ring-transparent focus:ring-0" />
                                {t('pageQuotation.step1.personnePhysique')}
                            </label>
                            <label className="flex items-center gap-2 cursor-pointer">
                                <input type="radio" name="customerType" value="morale" checked={customerType === 'morale'} onChange={() => setCustomerType('morale')} className="text-main checked:bg-main focus:ring-transparent focus:ring-0" />
                                {t('pageQuotation.step1.personneMorale')}
                            </label>
                        </div>
                    </div>

                    <PersonnalInfoFormQuotation customer={personnalInfoFormData} errors={errorsPersonnalInfo} updatePersonnalInfoFormData={updatePersonnalInfoFormData} />

                    <Hr />

                    <BillingInfoFormQuotation data={billingInfoFormData} errors={errorsBillingInfo} customerType={customerType} updateBillingInfoFormData={updateBillingInfoFormData} />
                </div>
            </div>

            <Hr />

            {!action && (
                <div className="flex flex-col-reverse md:flex-row md:justify-between gap-5 my-10 ">
                    <ButtonSecondary text={t('inputs.refuse')} action="refused" onClick={handleButtonActionClick} />
                    <Button text={t('inputs.next')} action="accept" onClick={handleButtonActionClick} />
                </div>
            )}

            {showModal && (
                <div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center">
                    <div className="bg-white rounded-xl p-6 w-full max-w-xl shadow-lg">
                        <H3 title={t('pageQuotation.acceptCGV.title')} className="mb-4" />
                        <Hr />

                        <Checkbox label="checkbox1" onChange={setCheckbox1Checked}>
                            <div className="block items-center">
                                <span className='text-description'>{t('pageQuotation.acceptCGV.text')}</span>&nbsp;
                                <button onClick={handleDownloadQuotationCGV} className='contents'>
                                    <span className="text-main contents font-semibold">{t('pageQuotation.acceptCGV.link')}</span>&nbsp;
                                </button>
                                <span className='text-description'>{t('pageQuotation.acceptCGV.text2')}</span>
                            </div>
                        </Checkbox>
                        {checkboxError && (
                            <p className="mt-1 text-sm text-error ml-1">{t('errors.checkboxError')}</p>
                        )}

                        <Hr />

                        <div className="mt-6 flex justify-end gap-4">
                            <ButtonSecondary text={t('inputs.refuse')} onClick={() => setShowModal(false)} />
                            <Button text={t('inputs.accept')} action="acceptCGV" onClick={handleButtonActionClick} />
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
};